/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Image,
  ActivityIndicator,
  Alert,
  Linking,
  Modal,
  RefreshControl,
  Animated,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — replace placeholder values with real keys
// ─────────────────────────────────────────────────────────────────────────────
const ANTHROPIC_API_KEY = 'YOUR_ANTHROPIC_API_KEY'; // Use a backend proxy in production

const UK_WORKPLACE_APIS = {
  reedEvents:        { baseUrl: 'https://www.reed.co.uk/api/events', apiKey: 'YOUR_REED_API_KEY' },
  indeedEvents:      { baseUrl: 'https://api.indeed.com/ads/apiservice', publisherId: 'YOUR_INDEED_PUBLISHER_ID' },
  meetup:            { baseUrl: 'https://api.meetup.com', apiKey: 'YOUR_MEETUP_API_KEY' },
  eventbrite:        { baseUrl: 'https://www.eventbriteapi.com/v3', oauthToken: 'YOUR_EVENTBRITE_TOKEN' },
  govUK:             { baseUrl: 'https://www.gov.uk/api' },
  professionalBodies: {
    iet:    'https://www.theiet.org/events/',
    ice:    'https://www.ice.org.uk/events',
    imeche: 'https://www.imeche.org/events',
    bcs:    'https://www.bcs.org/events/',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────────────────────
const UK_REGIONS = [
  'All UK','London','Manchester','Birmingham','Glasgow','Edinburgh',
  'Liverpool','Leeds','Bristol','Sheffield','Cardiff','Belfast',
  'Newcastle','Nottingham','Southampton','Online',
];

const ENGINEERING_SECTORS = [
  'All Sectors','Software Engineering','Civil Engineering','Mechanical Engineering',
  'Electrical Engineering','Aerospace','Automotive','Chemical Engineering',
  'Biomedical Engineering','Environmental Engineering','Renewable Energy',
  'Robotics & AI','Data Science','Cyber Security','Construction',
  'Manufacturing','Telecommunications',
];

const WORKPLACE_EVENT_TYPES = [
  'All Events','Job Fair','Career Fair','Recruitment Day','Networking Event',
  'Industry Conference','Workshop','Training Course','Seminar','Webinar',
  'Company Open Day','Graduate Scheme Event','Apprenticeship Fair',
  'Diversity & Inclusion Event','Women in Engineering','Professional Development',
  'Industry Exhibition','Hackathon','Tech Talk',
];

const SORT_OPTIONS = [
  { id: 'date',       label: 'Date (Soonest)',  icon: 'calendar-sort-ascending' },
  { id: 'popularity', label: 'Most Popular',    icon: 'trending-up' },
  { id: 'name',       label: 'Name (A–Z)',       icon: 'sort-alphabetical-ascending' },
  { id: 'price',      label: 'Free First',       icon: 'cash' },
];

const DATE_FILTERS = [
  { id: 'all',      label: 'Any Date' },
  { id: 'today',    label: 'Today' },
  { id: 'week',     label: 'This Week' },
  { id: 'month',    label: 'This Month' },
  { id: '3months',  label: 'Next 3 Months' },
];

const PRICE_FILTERS = [
  { id: 'all',  label: 'All Prices' },
  { id: 'free', label: 'Free Only' },
  { id: 'paid', label: 'Paid Only' },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────
const getEventTypeFromReed = (t) => ({
  'Career Fair': 'Career Fair', 'Job Fair': 'Job Fair', 'Networking': 'Networking Event',
  'Conference': 'Industry Conference', 'Workshop': 'Workshop', 'Seminar': 'Seminar',
  'Webinar': 'Webinar', 'Open Day': 'Company Open Day',
}[t] || 'Professional Event');

const getEventTypeFromMeetup = (group) => {
  const n = group.name;
  if (n.includes('Meetup') || n.includes('Networking')) return 'Networking Event';
  if (n.includes('Workshop') || n.includes('Training'))  return 'Workshop';
  if (n.includes('Conference') || n.includes('Summit'))  return 'Industry Conference';
  if (n.includes('Hackathon'))                            return 'Hackathon';
  return 'Tech Talk';
};

const getEventTypeFromEventbrite = (event) => {
  const n = event.name.text.toLowerCase();
  if (n.includes('job fair') || n.includes('career fair')) return 'Job Fair';
  if (n.includes('conference') || n.includes('summit'))    return 'Industry Conference';
  if (n.includes('workshop') || n.includes('training'))    return 'Workshop';
  if (n.includes('networking'))  return 'Networking Event';
  if (n.includes('webinar'))     return 'Webinar';
  if (n.includes('exhibition'))  return 'Industry Exhibition';
  return 'Professional Event';
};

const formatMeetupTime = (timestamp, duration) => {
  const start = new Date(timestamp);
  const end   = new Date(start.getTime() + (duration || 7200000));
  const fmt = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fmt(start)} - ${fmt(end)}`;
};

const formatEventTime = (startTime, endTime) => {
  try {
    if (!startTime) return '10:00 - 16:00';
    const start = new Date(startTime);
    const end   = endTime ? new Date(endTime) : new Date(start.getTime() + 6 * 3600000);
    const fmt = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${fmt(start)} - ${fmt(end)}`;
  } catch { return '10:00 - 16:00'; }
};

const generateTagsFromEvent = (event) => {
  const tags = [];
  const title = event.name.text.toLowerCase();
  const desc  = (event.description?.text || '').toLowerCase();
  if (event.online_event)                             tags.push('online');
  if (title.includes('tech') || desc.includes('technology')) tags.push('technology');
  if (title.includes('career') || title.includes('job'))     tags.push('career');
  if (title.includes('networking'))   tags.push('networking');
  if (title.includes('women'))        tags.push('women in engineering');
  if (title.includes('graduate'))     tags.push('graduate');
  if (title.includes('apprenticeship')) tags.push('apprenticeship');
  if (title.includes('diversity'))    tags.push('diversity');
  return tags.slice(0, 5);
};

// ─────────────────────────────────────────────────────────────────────────────
// API CALLS
// ─────────────────────────────────────────────────────────────────────────────
const fetchProfessionalBodiesEvents = async (sector) => {
  const events = [
    {
      id: 'prof_1', title: 'IET London: Faraday Lecture 2025', type: 'Industry Conference',
      date: '2025-03-15', time: '18:00 - 20:00', location: 'IET London: Savoy Place',
      organizer: 'Institution of Engineering and Technology',
      description: 'Annual Faraday Lecture featuring cutting-edge engineering innovations.',
      isFeatured: true, isRegistered: false, tags: ['iet','professional','lecture','innovation'],
      price: 'Free for members', attendees: 300, url: 'https://www.theiet.org/events/', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_2', title: 'ICE Annual Conference 2025', type: 'Industry Conference',
      date: '2025-04-22', time: '09:00 - 17:00', location: 'One Great George Street, London',
      organizer: 'Institution of Civil Engineers',
      description: 'UK\'s premier civil engineering conference with industry leaders.',
      isFeatured: true, isRegistered: false, tags: ['ice','civil engineering','conference','infrastructure'],
      price: 'From £250', attendees: 500, url: 'https://www.ice.org.uk/events', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_3', title: 'IMechE Manufacturing Excellence', type: 'Workshop',
      date: '2025-05-10', time: '10:00 - 16:00', location: 'Birdcage Walk, London',
      organizer: 'Institution of Mechanical Engineers',
      description: 'Workshop on manufacturing excellence and industry 4.0.',
      isFeatured: false, isRegistered: false, tags: ['imeche','manufacturing','workshop'],
      price: '£150', attendees: 80, url: 'https://www.imeche.org/events', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_4', title: 'BCS Tech Trends 2025', type: 'Tech Talk',
      date: '2025-06-05', time: '18:30 - 20:30', location: 'BCS London Office',
      organizer: 'British Computer Society',
      description: 'Monthly tech talk on emerging trends in computing.',
      isFeatured: false, isRegistered: false, tags: ['bcs','computing','tech talk'],
      price: 'Free', attendees: 60, url: 'https://www.bcs.org/events/', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_5', title: 'Women in Engineering Career Summit', type: 'Career Fair',
      date: '2025-07-10', time: '10:00 - 17:00', location: 'Manchester Central Convention Complex',
      organizer: 'Women\'s Engineering Society',
      description: 'Annual summit connecting women engineers with leading employers.',
      isFeatured: true, isRegistered: false, tags: ['women in engineering','career','networking','diversity'],
      price: 'Free', attendees: 600, url: 'https://www.wes.org.uk', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_6', title: 'UK Engineering Apprenticeship Fair 2025', type: 'Apprenticeship Fair',
      date: '2025-04-05', time: '10:00 - 15:00', location: 'NEC Birmingham',
      organizer: 'Engineering UK',
      description: 'Connect with employers offering engineering apprenticeships across the UK.',
      isFeatured: true, isRegistered: false, tags: ['apprenticeship','graduate','career','engineering'],
      price: 'Free', attendees: 800, url: 'https://www.engineeringuk.com', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_7', title: 'Hackathon: Green Engineering Challenge', type: 'Hackathon',
      date: '2025-05-24', time: '09:00 - 21:00', location: 'Online / London Hub',
      organizer: 'Innovate UK',
      description: '24-hour hackathon solving real sustainability engineering problems.',
      isFeatured: false, isRegistered: false, tags: ['hackathon','green','sustainability','renewable energy'],
      price: 'Free', attendees: 200, url: 'https://www.ukri.org', source: 'professional', isRealEvent: true,
    },
    {
      id: 'prof_8', title: 'UK Engineering Career Conference 2025', type: 'Industry Conference',
      date: '2025-06-15', time: '09:00 - 17:00', location: 'ExCeL London',
      organizer: 'UK Engineering Council',
      description: 'The premier engineering career event in the UK.',
      isFeatured: true, isRegistered: false, tags: ['engineering','career','conference','networking'],
      price: 'Free', attendees: 1250, url: 'https://www.engineeringcouncil.org.uk/events', source: 'professional', isRealEvent: true,
    },
  ];
  if (sector !== 'All Sectors') {
    return events.filter(e => e.tags.some(t => t.toLowerCase().includes(sector.toLowerCase())));
  }
  return events;
};

const fetchUKWorkplaceEvents = async (location = 'All UK', sector = 'All Sectors', eventType = 'All Events') => {
  try {
    let allEvents = [];
    const today  = new Date().toISOString().split('T')[0];
    const endD   = new Date(); endD.setMonth(endD.getMonth() + 3);
    const endDate = endD.toISOString().split('T')[0];

    // Reed
    if (UK_WORKPLACE_APIS.reedEvents.apiKey !== 'YOUR_REED_API_KEY') {
      try {
        let url = `${UK_WORKPLACE_APIS.reedEvents.baseUrl}?dateFrom=${today}&dateTo=${endDate}&resultsToTake=50`;
        if (location !== 'All UK' && location !== 'Online') url += `&location=${encodeURIComponent(location)}`;
        if (sector !== 'All Sectors') url += `&keywords=${encodeURIComponent(sector)}`;
        const res = await fetch(url, { headers: { 'Authorization': `Basic ${btoa(UK_WORKPLACE_APIS.reedEvents.apiKey + ':')}`, 'Content-Type': 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          if (data.results?.length) {
            allEvents = [...allEvents, ...data.results.map(e => ({
              id: `reed_${e.eventId}`, title: e.eventTitle, type: getEventTypeFromReed(e.eventType),
              date: e.eventDate, time: e.eventTime || '10:00 - 16:00',
              location: `${e.locationName}, ${e.locationTown}`, organizer: e.organiserName || 'Reed Events',
              description: e.eventDescription, isFeatured: e.isFeatured || false, isRegistered: false,
              tags: ['reed','uk','workplace', e.eventType?.toLowerCase() || 'event'],
              price: e.cost === '0' ? 'Free' : `£${e.cost}`, attendees: e.expectedAttendees || 50,
              url: e.eventUrl, source: 'reed', isRealEvent: true,
            }))];
          }
        }
      } catch (err) { console.error('Reed API Error:', err); }
    }

    // Meetup
    if (UK_WORKPLACE_APIS.meetup.apiKey !== 'YOUR_MEETUP_API_KEY') {
      try {
        let url = `${UK_WORKPLACE_APIS.meetup.baseUrl}/find/upcoming_events?photo-host=public&page=20&text=engineering+OR+technology+OR+career`;
        if (location !== 'All UK') url += `&lat=51.5074&lon=-0.1278&radius=50`;
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${UK_WORKPLACE_APIS.meetup.apiKey}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.events?.length) {
            allEvents = [...allEvents, ...data.events.map(e => ({
              id: `meetup_${e.id}`, title: e.name, type: getEventTypeFromMeetup(e.group),
              date: new Date(e.time).toISOString().split('T')[0],
              time: formatMeetupTime(e.time, e.duration),
              location: e.venue ? `${e.venue.name}, ${e.venue.city}` : 'Online Event',
              organizer: e.group.name, description: e.description || 'Join us for networking and learning.',
              isFeatured: e.yes_rsvp_count > 50, isRegistered: false,
              tags: ['meetup','networking','tech', ...(e.group.category ? [e.group.category.name] : [])],
              price: e.fee ? `£${e.fee.amount}` : 'Free', attendees: e.yes_rsvp_count || 20,
              url: e.link, source: 'meetup', isRealEvent: true,
            }))];
          }
        }
      } catch (err) { console.error('Meetup API Error:', err); }
    }

    // Eventbrite
    if (UK_WORKPLACE_APIS.eventbrite.oauthToken !== 'YOUR_EVENTBRITE_TOKEN') {
      try {
        const url = `${UK_WORKPLACE_APIS.eventbrite.baseUrl}/events/search/?q=engineering+OR+technology+OR+career&location.address=${encodeURIComponent(location === 'All UK' ? 'United Kingdom' : location + ' UK')}&sort_by=date&expand=venue,logo,organizer&start_date.range_start=${today}&start_date.range_end=${endDate}`;
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${UK_WORKPLACE_APIS.eventbrite.oauthToken}`, 'Content-Type': 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          if (data.events?.length) {
            allEvents = [...allEvents, ...data.events.map(e => ({
              id: `eventbrite_${e.id}`, title: e.name.text, type: getEventTypeFromEventbrite(e),
              date: e.start.local.split('T')[0],
              time: formatEventTime(e.start.local, e.end.local),
              location: e.venue ? `${e.venue.name}, ${e.venue.address.city}` : 'Online',
              organizer: e.organizer.name, description: e.description.text || 'Details on Eventbrite.',
              isFeatured: e.capacity && e.capacity > 100, isRegistered: false,
              tags: ['eventbrite','professional', ...generateTagsFromEvent(e)],
              price: e.is_free ? 'Free' : 'Paid', attendees: e.capacity || 100,
              url: e.url, source: 'eventbrite', isRealEvent: true,
            }))];
          }
        }
      } catch (err) { console.error('Eventbrite API Error:', err); }
    }

    const profEvents = await fetchProfessionalBodiesEvents(sector);
    allEvents = [...allEvents, ...profEvents];

    if (eventType !== 'All Events') {
      allEvents = allEvents.filter(e =>
        e.type.toLowerCase().includes(eventType.toLowerCase()) ||
        e.tags.some(t => t.toLowerCase().includes(eventType.toLowerCase()))
      );
    }

    // Deduplicate
    const seen = new Set();
    const unique = allEvents.filter(e => {
      const k = `${e.title}-${e.date}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });

    unique.sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      events: unique.slice(0, 60),
      totalFound: unique.length,
      sources: {
        reed:         unique.filter(e => e.source === 'reed').length,
        meetup:       unique.filter(e => e.source === 'meetup').length,
        eventbrite:   unique.filter(e => e.source === 'eventbrite').length,
        professional: unique.filter(e => e.source === 'professional').length,
      },
      message: `Found ${unique.length} UK workplace events`,
    };
  } catch (err) {
    console.error('fetchUKWorkplaceEvents error:', err);
    return { events: [], totalFound: 0, sources: {}, message: 'Error fetching events.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// REAL CLAUDE AI ADVICE
// ─────────────────────────────────────────────────────────────────────────────
const getClaudeAIAdvice = async (sector, location, experienceLevel, eventCount) => {
  if (ANTHROPIC_API_KEY === 'YOUR_ANTHROPIC_API_KEY') {
    // Fallback template advice
    const templates = {
      'Software Engineering': { graduate: `Graduate software engineers in ${location}: Attend grad career fairs, tech meetups, and AWS/Google Cloud events. Build your GitHub portfolio and network with seniors.`, mid: `Mid-level engineers in ${location}: Target Devoxx UK, QCon London, and leadership workshops. Consider speaking at local meetups to raise your profile.`, senior: `Senior engineers: Focus on CTO summits, architecture conferences, and board-level networking. Mentor at graduate events.` },
      'Civil Engineering':    { graduate: `Civil graduates in ${location}: Attend ICE graduate events, construction fairs, and site open days. Network with Balfour Beatty, Kier, Morgan Sindall.`, mid: `Mid-career civil engineers: ICE regional events, infrastructure conferences, BIM workshops — essential for chartership.`, senior: `Senior civil engineers: ICE Council events, government infrastructure forums, major project launches.` },
    };
    const t = templates[sector] || {};
    const level = experienceLevel === 'executive' ? 'senior' : experienceLevel;
    return t[level] || `For ${sector} professionals in ${location} (${experienceLevel} level):\n\n• Attend relevant professional body events (IET, ICE, IMechE, BCS)\n• Join industry-specific networking groups\n• Participate in CPD events\n• Consider speaking opportunities\n• Track ${eventCount} upcoming events in your area`;
  }
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 400,
        messages: [{
          role: 'user',
          content: `Give concise, actionable UK engineering career advice (5 bullet points, max 200 words) for a ${experienceLevel}-level ${sector} professional in ${location}. There are ${eventCount} upcoming events available. Focus on which types of events to prioritise, how to maximise networking ROI, and specific UK professional bodies to engage with.`,
        }],
      }),
    });
    const data = await response.json();
    return data.content?.[0]?.text || 'Unable to load AI advice at this time.';
  } catch (err) {
    return `AI advice unavailable. Focus on attending professional body events and networking actively in ${location}.`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// LOGO
// ─────────────────────────────────────────────────────────────────────────────
const JobengLogo = () => (
  <Image source={require('../assets/jobeng_icon.png')} style={{ width: 30, height: 30 }} resizeMode="contain" />
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function EventsScreen() {
  const navigation = useNavigation();

  // Core data
  const [events,         setEvents]         = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);

  // Filters & search
  const [searchQuery,        setSearchQuery]        = useState('');
  const [selectedLocation,   setSelectedLocation]   = useState('All UK');
  const [selectedSector,     setSelectedSector]     = useState('All Sectors');
  const [selectedEventType,  setSelectedEventType]  = useState('All Events');
  const [experienceLevel,    setExperienceLevel]    = useState('mid');
  const [sortBy,             setSortBy]             = useState('date');
  const [priceFilter,        setPriceFilter]        = useState('all');
  const [dateFilter,         setDateFilter]         = useState('all');
  const [showFilters,        setShowFilters]        = useState(false);
  const [viewMode,           setViewMode]           = useState('list'); // 'list' | 'grid'

  // Saved / bookmarks
  const [savedEventIds, setSavedEventIds] = useState(new Set());
  const [activeTab,     setActiveTab]     = useState('all'); // 'all' | 'saved'

  // UI state
  const [apiStatus,    setApiStatus]    = useState('Connecting to UK workplace events...');
  const [apiSources,   setApiSources]   = useState({});
  const [aiAdvice,     setAiAdvice]     = useState('');
  const [aiLoading,    setAiLoading]    = useState(false);
  const [showAiModal,  setShowAiModal]  = useState(false);
  const [showSortModal,setShowSortModal]= useState(false);

  const experienceLevels = ['student','graduate','mid','senior','executive'];

  // ── Load & filter ─────────────────────────────────────────────────────────
  useEffect(() => { loadEvents(); }, [selectedLocation, selectedSector, selectedEventType]);
  useEffect(() => { applyFilters(); }, [searchQuery, events, sortBy, priceFilter, dateFilter, activeTab, savedEventIds]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setApiStatus(`Searching UK workplace events in ${selectedLocation}...`);
      const data = await fetchUKWorkplaceEvents(selectedLocation, selectedSector, selectedEventType);
      setEvents(data.events);
      setApiSources(data.sources);
      setApiStatus(`Found ${data.totalFound} events`);
    } catch (err) {
      console.error(err);
      setApiStatus('Error loading events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadEvents().finally(() => setRefreshing(false));
  }, [selectedLocation, selectedSector, selectedEventType]);

  const applyFilters = () => {
    let list = activeTab === 'saved'
      ? events.filter(e => savedEventIds.has(e.id))
      : [...events];

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        e.title.toLowerCase().includes(q) ||
        e.organizer.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.tags?.some(t => t.toLowerCase().includes(q))
      );
    }

    // Price
    if (priceFilter === 'free') list = list.filter(e => e.price.toLowerCase().includes('free'));
    if (priceFilter === 'paid') list = list.filter(e => !e.price.toLowerCase().includes('free'));

    // Date
    const now = new Date(); now.setHours(0,0,0,0);
    if (dateFilter === 'today') {
      list = list.filter(e => new Date(e.date).toDateString() === now.toDateString());
    } else if (dateFilter === 'week') {
      const end = new Date(now); end.setDate(end.getDate() + 7);
      list = list.filter(e => { const d = new Date(e.date); return d >= now && d <= end; });
    } else if (dateFilter === 'month') {
      const end = new Date(now); end.setMonth(end.getMonth() + 1);
      list = list.filter(e => { const d = new Date(e.date); return d >= now && d <= end; });
    } else if (dateFilter === '3months') {
      const end = new Date(now); end.setMonth(end.getMonth() + 3);
      list = list.filter(e => { const d = new Date(e.date); return d >= now && d <= end; });
    }

    // Sort
    if (sortBy === 'date')       list.sort((a,b) => new Date(a.date) - new Date(b.date));
    if (sortBy === 'popularity') list.sort((a,b) => (b.attendees||0) - (a.attendees||0));
    if (sortBy === 'name')       list.sort((a,b) => a.title.localeCompare(b.title));
    if (sortBy === 'price')      list.sort((a,b) => {
      const aF = a.price.toLowerCase().includes('free') ? 0 : 1;
      const bF = b.price.toLowerCase().includes('free') ? 0 : 1;
      return aF - bF;
    });

    setFilteredEvents(list);
  };

  const toggleRegistration = (id) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, isRegistered: !e.isRegistered } : e));
  };

  const toggleSaved = (id) => {
    setSavedEventIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleViewEvent = (event) => {
    navigation.navigate('EventDetail', { eventId: event.id, eventData: event });
  };

  const handleGetAiAdvice = async () => {
    setShowAiModal(true);
    if (!aiAdvice) {
      setAiLoading(true);
      const advice = await getClaudeAIAdvice(selectedSector, selectedLocation, experienceLevel, filteredEvents.length);
      setAiAdvice(advice);
      setAiLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery(''); setSelectedLocation('All UK'); setSelectedSector('All Sectors');
    setSelectedEventType('All Events'); setSortBy('date'); setPriceFilter('all');
    setDateFilter('all'); setExperienceLevel('mid');
  };

  // ── Colors & Icons ────────────────────────────────────────────────────────
  const getEventColor = (type) => ({
    'Job Fair': '#19abb5', 'Career Fair': '#0e4e96', 'Networking Event': '#f59e0b',
    'Industry Conference': '#8b5cf6', 'Workshop': '#fdb913', 'Webinar': '#10b981',
    'Company Open Day': '#ef4444', 'Tech Talk': '#3b82f6', 'Hackathon': '#6d28d9',
    'Industry Exhibition': '#06b6d4', 'Apprenticeship Fair': '#db2777',
    'Graduate Scheme Event': '#059669', 'Women in Engineering': '#d946ef',
  }[type] || '#0e4e96');

  const getEventIcon = (type) => ({
    'Job Fair': 'briefcase-search', 'Career Fair': 'account-tie', 'Networking Event': 'account-group',
    'Industry Conference': 'microphone-variant', 'Workshop': 'hammer-wrench', 'Webinar': 'video',
    'Company Open Day': 'office-building', 'Tech Talk': 'presentation', 'Hackathon': 'code-braces',
    'Industry Exhibition': 'view-gallery', 'Apprenticeship Fair': 'school', 'Graduate Scheme Event': 'school-outline',
    'Women in Engineering': 'gender-female', 'Recruitment Day': 'clipboard-account',
  }[type] || 'calendar-month');

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  // ── Stats strip ───────────────────────────────────────────────────────────
  const stats = {
    total:    events.length,
    free:     events.filter(e => e.price.toLowerCase().includes('free')).length,
    thisWeek: events.filter(e => {
      const d = new Date(e.date); const now = new Date(); const end = new Date();
      end.setDate(end.getDate() + 7);
      return d >= now && d <= end;
    }).length,
    featured: events.filter(e => e.isFeatured).length,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderFeaturedCarousel = () => {
    const featured = events.filter(e => e.isFeatured).slice(0, 6);
    if (!featured.length) return null;
    return (
      <View style={styles.featuredSection}>
        <Text style={styles.featuredTitle}>⭐ Featured Events</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {featured.map(item => (
            <TouchableOpacity key={item.id} style={styles.featuredCard} onPress={() => handleViewEvent(item)}>
              <View style={[styles.featuredCardBanner, { backgroundColor: getEventColor(item.type) }]}>
                <MaterialCommunityIcons name={getEventIcon(item.type)} size={32} color="#fff" />
              </View>
              <View style={styles.featuredCardBody}>
                <Text style={styles.featuredCardTitle} numberOfLines={2}>{item.title}</Text>
                <Text style={styles.featuredCardDate}>{formatDate(item.date)}</Text>
                <Text style={styles.featuredCardLocation} numberOfLines={1}>{item.location}</Text>
                <View style={[styles.featuredPriceBadge, { backgroundColor: item.price.toLowerCase().includes('free') ? '#dcfce7' : '#fef3c7' }]}>
                  <Text style={[styles.featuredPriceText, { color: item.price.toLowerCase().includes('free') ? '#166534' : '#92400e' }]}>{item.price}</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderStatsStrip = () => (
    <View style={styles.statsStrip}>
      {[
        { label: 'Total',      value: stats.total,    icon: 'calendar-multiple', color: '#fff' },
        { label: 'Free',       value: stats.free,     icon: 'currency-gbp',      color: '#4ade80' },
        { label: 'This Week',  value: stats.thisWeek, icon: 'clock-fast',        color: '#fbbf24' },
        { label: 'Featured',   value: stats.featured, icon: 'star',              color: '#f472b6' },
      ].map(s => (
        <View key={s.label} style={styles.statItem}>
          <MaterialCommunityIcons name={s.icon} size={18} color={s.color} />
          <Text style={[styles.statValue, { color: s.color }]}>{s.value}</Text>
          <Text style={styles.statLabel}>{s.label}</Text>
        </View>
      ))}
    </View>
  );

  const renderApiSources = () => {
    if (!apiSources || !Object.keys(apiSources).length) return null;
    return (
      <View style={styles.sourcesContainer}>
        <Text style={styles.sourcesTitle}>Sources:</Text>
        <View style={styles.sourcesList}>
          {Object.entries(apiSources).map(([src, cnt]) => cnt > 0 && (
            <View key={src} style={styles.sourceBadge}>
              <Text style={styles.sourceText}>{src.charAt(0).toUpperCase() + src.slice(1)}: {cnt}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderFilterChips = (options, selected, onSelect, key='id', labelKey='label') => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll}>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : opt[key];
        const lbl = typeof opt === 'string' ? opt : opt[labelKey];
        return (
          <TouchableOpacity key={val} style={[styles.filterChip, selected === val && styles.filterChipSelected]} onPress={() => onSelect(val)}>
            <Text style={[styles.filterChipText, selected === val && styles.filterChipTextSelected]}>{lbl}</Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const renderEventCard = ({ item }) => {
    const color  = getEventColor(item.type);
    const icon   = getEventIcon(item.type);
    const isSaved = savedEventIds.has(item.id);
    const isFree  = item.price.toLowerCase().includes('free');
    const isOnline= item.location.toLowerCase().includes('online');

    if (viewMode === 'grid') {
      return (
        <TouchableOpacity style={styles.gridCard} onPress={() => handleViewEvent(item)}>
          <View style={[styles.gridCardBanner, { backgroundColor: color }]}>
            <MaterialCommunityIcons name={icon} size={28} color="#fff" />
            <TouchableOpacity style={styles.gridSaveBtn} onPress={() => toggleSaved(item.id)}>
              <MaterialCommunityIcons name={isSaved ? 'bookmark' : 'bookmark-outline'} size={18} color="#fff" />
            </TouchableOpacity>
          </View>
          <View style={styles.gridCardBody}>
            <Text style={styles.gridCardTitle} numberOfLines={2}>{item.title}</Text>
            <Text style={styles.gridCardDate}>{formatDate(item.date)}</Text>
            <Text style={styles.gridCardLocation} numberOfLines={1}>{item.location}</Text>
            <View style={styles.gridCardFooter}>
              <View style={[styles.priceBadge, { backgroundColor: isFree ? '#dcfce7' : '#fef3c7' }]}>
                <Text style={[styles.priceText, { color: isFree ? '#166534' : '#92400e' }]}>{item.price}</Text>
              </View>
              {isOnline && <View style={styles.onlineBadge}><Text style={styles.onlineBadgeText}>Online</Text></View>}
            </View>
          </View>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity style={styles.eventCard} onPress={() => handleViewEvent(item)}>
        <View style={styles.eventCardTop}>
          <View style={[styles.eventIconContainer, { backgroundColor: color }]}>
            <MaterialCommunityIcons name={icon} size={24} color="#fff" />
          </View>
          <View style={styles.eventHeaderContent}>
            <View style={styles.eventTitleRow}>
              <Text style={styles.eventTitle} numberOfLines={2}>{item.title}</Text>
              <TouchableOpacity onPress={() => toggleSaved(item.id)} style={styles.saveBtn}>
                <MaterialCommunityIcons name={isSaved ? 'bookmark' : 'bookmark-outline'} size={22} color={isSaved ? '#0e4e96' : '#aaa'} />
              </TouchableOpacity>
            </View>
            <Text style={styles.eventOrganizer}>{item.organizer}</Text>
            <View style={styles.badgeRow}>
              <View style={[styles.sourceBadgeSmall, { backgroundColor: color + '22' }]}>
                <Text style={[styles.sourceBadgeText, { color }]}>{item.source}</Text>
              </View>
              {item.isFeatured && (
                <View style={styles.featuredBadge}>
                  <MaterialCommunityIcons name="star" size={10} color="#f59e0b" />
                  <Text style={styles.featuredBadgeText}>Featured</Text>
                </View>
              )}
              {isOnline && <View style={styles.onlineBadge}><Text style={styles.onlineBadgeText}>Online</Text></View>}
            </View>
          </View>
        </View>

        <View style={styles.eventDetails}>
          {[
            { icon: 'calendar',      text: formatDate(item.date) },
            { icon: 'clock-outline', text: item.time },
            { icon: 'map-marker',    text: item.location, lines: 1 },
            { icon: 'account-group', text: `${item.attendees || '?'} attendees` },
          ].map((d,i) => (
            <View key={i} style={styles.eventDetail}>
              <MaterialCommunityIcons name={d.icon} size={15} color="#666" />
              <Text style={styles.eventDetailText} numberOfLines={d.lines}>{d.text}</Text>
            </View>
          ))}
        </View>

        {item.tags?.length > 0 && (
          <View style={styles.eventTags}>
            {item.tags.slice(0, 4).map((tag, i) => (
              <View key={i} style={styles.tag}><Text style={styles.tagText}>{tag}</Text></View>
            ))}
          </View>
        )}

        <View style={styles.eventActions}>
          <View style={[styles.priceBadgeLarge, { backgroundColor: isFree ? '#dcfce7' : '#fef3c7' }]}>
            <Text style={[styles.priceTextLarge, { color: isFree ? '#166534' : '#92400e' }]}>{item.price}</Text>
          </View>
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.registerButton, item.isRegistered && styles.registeredButton]}
              onPress={() => toggleRegistration(item.id)}
            >
              <MaterialCommunityIcons name={item.isRegistered ? 'check-circle' : 'clipboard-check'} size={14} color="#fff" />
              <Text style={styles.registerButtonText}>{item.isRegistered ? 'Registered' : 'Register'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.viewButton} onPress={() => handleViewEvent(item)}>
              <Text style={styles.viewButtonText}>Details</Text>
              <MaterialCommunityIcons name="chevron-right" size={14} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const hasActiveFilters = selectedLocation !== 'All UK' || selectedSector !== 'All Sectors' ||
    selectedEventType !== 'All Events' || priceFilter !== 'all' || dateFilter !== 'all' || sortBy !== 'date';

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* ── Header ── */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <JobengLogo />
          <Text style={styles.headerTitle}>UK Workplace Events</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={() => setShowSortModal(true)} style={styles.headerBtn}>
            <MaterialCommunityIcons name="sort" size={22} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleGetAiAdvice} style={styles.headerBtn}>
            <MaterialCommunityIcons name="robot" size={22} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#fff" />}
      >
        <View style={styles.container}>
          {/* ── Title ── */}
          <Text style={styles.heading}>Discover UK Engineering Events</Text>
          <Text style={styles.subheading}>Real-time events from job fairs, conferences & professional bodies</Text>

          {/* ── Status ── */}
          <View style={styles.statusContainer}>
            <MaterialCommunityIcons name="information-outline" size={18} color="#19abb5" />
            <Text style={styles.statusText}>{apiStatus}</Text>
          </View>

          {/* ── Stats ── */}
          {!loading && renderStatsStrip()}

          {/* ── Sources ── */}
          {renderApiSources()}

          {/* ── Search ── */}
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={20} color="#666" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search events, companies, locations..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#999"
            />
            {searchQuery ? (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color="#999" />
              </TouchableOpacity>
            ) : null}
          </View>

          {/* ── Filter Tabs ── */}
          <View style={styles.filterBar}>
            <TouchableOpacity
              style={[styles.filterToggleBtn, hasActiveFilters && styles.filterToggleBtnActive]}
              onPress={() => setShowFilters(!showFilters)}
            >
              <MaterialCommunityIcons name="filter-variant" size={16} color={hasActiveFilters ? '#0e4e96' : '#fff'} />
              <Text style={[styles.filterToggleText, hasActiveFilters && styles.filterToggleTextActive]}>
                Filters{hasActiveFilters ? ' •' : ''}
              </Text>
            </TouchableOpacity>
            <View style={styles.viewToggle}>
              {(['list','grid']).map(m => (
                <TouchableOpacity key={m} style={[styles.viewBtn, viewMode === m && styles.viewBtnActive]} onPress={() => setViewMode(m)}>
                  <MaterialCommunityIcons name={m === 'list' ? 'view-list' : 'view-grid'} size={18} color={viewMode === m ? '#0e4e96' : '#fff'} />
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {showFilters && (
            <View style={styles.filtersPanel}>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>📍 Location</Text>
                {renderFilterChips(UK_REGIONS, selectedLocation, setSelectedLocation)}
              </View>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>🏭 Sector</Text>
                {renderFilterChips(ENGINEERING_SECTORS, selectedSector, setSelectedSector)}
              </View>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>🎯 Event Type</Text>
                {renderFilterChips(WORKPLACE_EVENT_TYPES, selectedEventType, setSelectedEventType)}
              </View>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>👤 Experience</Text>
                {renderFilterChips(experienceLevels.map(l => ({ id: l, label: l.charAt(0).toUpperCase()+l.slice(1) })), experienceLevel, setExperienceLevel)}
              </View>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>📅 Date Range</Text>
                {renderFilterChips(DATE_FILTERS, dateFilter, setDateFilter)}
              </View>
              <View style={styles.filterSectionRow}>
                <Text style={styles.filterLabel}>💷 Price</Text>
                {renderFilterChips(PRICE_FILTERS, priceFilter, setPriceFilter)}
              </View>
              {hasActiveFilters && (
                <TouchableOpacity style={styles.clearFiltersBtn} onPress={resetFilters}>
                  <MaterialCommunityIcons name="filter-off" size={16} color="#ef4444" />
                  <Text style={styles.clearFiltersText}>Clear All Filters</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* ── Tab bar (All / Saved) ── */}
          <View style={styles.tabBar}>
            {[
              { id: 'all',   label: `All Events (${events.length})` },
              { id: 'saved', label: `Saved (${savedEventIds.size})` },
            ].map(t => (
              <TouchableOpacity key={t.id} style={[styles.tabBarBtn, activeTab === t.id && styles.tabBarBtnActive]} onPress={() => setActiveTab(t.id)}>
                <Text style={[styles.tabBarText, activeTab === t.id && styles.tabBarTextActive]}>{t.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* ── Featured Carousel ── */}
          {!loading && activeTab === 'all' && renderFeaturedCarousel()}

          {/* ── Sort indicator ── */}
          <View style={styles.listHeader}>
            <Text style={styles.listCount}>{filteredEvents.length} events</Text>
            <TouchableOpacity style={styles.sortChip} onPress={() => setShowSortModal(true)}>
              <MaterialCommunityIcons name="sort" size={14} color="#0e4e96" />
              <Text style={styles.sortChipText}>{SORT_OPTIONS.find(s=>s.id===sortBy)?.label}</Text>
            </TouchableOpacity>
          </View>

          {/* ── Events List / Grid ── */}
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size={40} color="#fff" />
              <Text style={styles.loadingText}>Loading UK events...</Text>
            </View>
          ) : filteredEvents.length === 0 ? (
            <View style={styles.noEventsContainer}>
              <MaterialCommunityIcons name="calendar-search" size={64} color="rgba(255,255,255,0.4)" />
              <Text style={styles.noEventsText}>
                {activeTab === 'saved' ? 'No saved events yet' : 'No events found'}
              </Text>
              <Text style={styles.noEventsSubtext}>
                {activeTab === 'saved' ? 'Bookmark events to save them here' : 'Try adjusting your filters or search terms'}
              </Text>
              {hasActiveFilters && (
                <TouchableOpacity style={styles.retryButton} onPress={resetFilters}>
                  <Text style={styles.retryButtonText}>Clear Filters</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[styles.retryButton, { marginTop: 10 }]} onPress={loadEvents}>
                <Text style={styles.retryButtonText}>Refresh Events</Text>
              </TouchableOpacity>
            </View>
          ) : viewMode === 'grid' ? (
            <View style={styles.gridContainer}>
              {filteredEvents.map(item => (
                <View key={item.id} style={styles.gridItem}>
                  {renderEventCard({ item })}
                </View>
              ))}
            </View>
          ) : (
            <FlatList
              data={filteredEvents}
              renderItem={renderEventCard}
              keyExtractor={item => item.id}
              scrollEnabled={false}
              contentContainerStyle={styles.eventsList}
            />
          )}
        </View>
      </ScrollView>

      {/* ── Sort Modal ── */}
      <Modal visible={showSortModal} animationType="slide" transparent>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowSortModal(false)}>
          <View style={styles.sortModalContent}>
            <Text style={styles.sortModalTitle}>Sort Events</Text>
            {SORT_OPTIONS.map(opt => (
              <TouchableOpacity
                key={opt.id}
                style={[styles.sortOption, sortBy === opt.id && styles.sortOptionActive]}
                onPress={() => { setSortBy(opt.id); setShowSortModal(false); }}
              >
                <MaterialCommunityIcons name={opt.icon} size={20} color={sortBy === opt.id ? '#0e4e96' : '#555'} />
                <Text style={[styles.sortOptionText, sortBy === opt.id && styles.sortOptionTextActive]}>{opt.label}</Text>
                {sortBy === opt.id && <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ── AI Advice Modal ── */}
      <Modal visible={showAiModal} animationType="slide" transparent>
        <View style={styles.modalContainer}>
          <View style={styles.aiModalContent}>
            <View style={styles.modalHeader}>
              <View style={styles.modalHeaderLeft}>
                <View style={styles.aiIcon}><MaterialCommunityIcons name="robot" size={22} color="#fff" /></View>
                <Text style={styles.modalTitle}>AI Career Strategy</Text>
              </View>
              <TouchableOpacity onPress={() => setShowAiModal(false)}>
                <Ionicons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            <View style={styles.aiContextRow}>
              <View style={styles.aiContextBadge}><Text style={styles.aiContextText}>{selectedSector}</Text></View>
              <View style={styles.aiContextBadge}><Text style={styles.aiContextText}>{selectedLocation}</Text></View>
              <View style={styles.aiContextBadge}><Text style={styles.aiContextText}>{experienceLevel}</Text></View>
            </View>
            <ScrollView style={styles.aiScrollBody}>
              {aiLoading ? (
                <View style={styles.aiLoadingBox}>
                  <ActivityIndicator size={28} color="#0e4e96" />
                  <Text style={styles.aiLoadingText}>Generating personalised advice...</Text>
                </View>
              ) : (
                <Text style={styles.aiAdviceText}>{aiAdvice || `Based on your selection (${selectedSector} in ${selectedLocation}), focus on networking events and professional development workshops.`}</Text>
              )}
              <View style={styles.tipsContainer}>
                <Text style={styles.tipsTitle}>⚡ Quick Tips</Text>
                {['Register early for popular events','Follow up with contacts within 48 hours','Prepare your elevator pitch (60 seconds)','Bring business cards or LinkedIn QR code','Research exhibiting companies beforehand','Log CPD hours for attended sessions'].map((tip,i) => (
                  <View key={i} style={styles.tipRow}>
                    <MaterialCommunityIcons name="check-circle-outline" size={16} color="#19abb5" />
                    <Text style={styles.tipText}>{tip}</Text>
                  </View>
                ))}
              </View>
            </ScrollView>
            <TouchableOpacity style={styles.aiRefreshBtn} onPress={async () => {
              setAiAdvice(''); setAiLoading(true);
              const a = await getClaudeAIAdvice(selectedSector, selectedLocation, experienceLevel, filteredEvents.length);
              setAiAdvice(a); setAiLoading(false);
            }}>
              <MaterialCommunityIcons name="refresh" size={16} color="#fff" />
              <Text style={styles.aiRefreshText}>Refresh Advice</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalButton} onPress={() => setShowAiModal(false)}>
              <Text style={styles.modalButtonText}>Got it!</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea:            { flex: 1, backgroundColor: '#0e4e96' },
  header:              { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#0e4e96', paddingVertical: 15, paddingHorizontal: 15 },
  headerCenter:        { flexDirection: 'row', alignItems: 'center' },
  headerTitle:         { color: '#fff', fontSize: 18, fontWeight: 'bold', marginLeft: 8 },
  headerActions:       { flexDirection: 'row' },
  headerBtn:           { padding: 6, marginLeft: 4 },
  backButton:          { padding: 5 },
  scrollView:          { flex: 1 },
  scrollViewContent:   { paddingBottom: 40 },
  container:           { padding: 15 },
  heading:             { fontSize: 22, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  subheading:          { fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 16 },
  statusContainer:     { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', padding: 10, borderRadius: 8, marginBottom: 12 },
  statusText:          { color: '#fff', marginLeft: 8, fontSize: 13, flex: 1 },
  // Stats
  statsStrip:          { flexDirection: 'row', backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: 12, marginBottom: 14, justifyContent: 'space-around' },
  statItem:            { alignItems: 'center' },
  statValue:           { fontSize: 18, fontWeight: 'bold', marginTop: 2 },
  statLabel:           { fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 1 },
  // Sources
  sourcesContainer:    { backgroundColor: 'rgba(255,255,255,0.1)', padding: 10, borderRadius: 8, marginBottom: 12 },
  sourcesTitle:        { color: '#fff', fontWeight: '600', marginBottom: 6, fontSize: 12 },
  sourcesList:         { flexDirection: 'row', flexWrap: 'wrap' },
  sourceBadge:         { backgroundColor: 'rgba(25,171,181,0.35)', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12, marginRight: 6, marginBottom: 4 },
  sourceText:          { color: '#fff', fontSize: 11 },
  // Search
  searchContainer:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 10, paddingHorizontal: 14, marginBottom: 14, height: 48 },
  searchInput:         { flex: 1, height: 48, fontSize: 15, color: '#333', marginLeft: 8 },
  // Filter bar
  filterBar:           { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  filterToggleBtn:     { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.15)', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20 },
  filterToggleBtnActive:{ backgroundColor: '#fff' },
  filterToggleText:    { color: '#fff', marginLeft: 6, fontWeight: '500', fontSize: 13 },
  filterToggleTextActive:{ color: '#0e4e96' },
  viewToggle:          { flexDirection: 'row', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, overflow: 'hidden' },
  viewBtn:             { padding: 8, paddingHorizontal: 12 },
  viewBtnActive:       { backgroundColor: '#fff' },
  // Filters panel
  filtersPanel:        { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: 14, marginBottom: 14 },
  filterSectionRow:    { marginBottom: 12 },
  filterLabel:         { color: '#fff', fontSize: 12, fontWeight: '600', marginBottom: 6 },
  filterScroll:        { flexGrow: 0 },
  filterChip:          { backgroundColor: 'rgba(255,255,255,0.15)', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, marginRight: 8 },
  filterChipSelected:  { backgroundColor: '#fff' },
  filterChipText:      { color: '#fff', fontSize: 12, fontWeight: '500' },
  filterChipTextSelected:{ color: '#0e4e96', fontWeight: '700' },
  clearFiltersBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 6, padding: 10, backgroundColor: 'rgba(239,68,68,0.15)', borderRadius: 8 },
  clearFiltersText:    { color: '#ef4444', marginLeft: 6, fontWeight: '600', fontSize: 13 },
  // Tab bar
  tabBar:              { flexDirection: 'row', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: 3, marginBottom: 14 },
  tabBarBtn:           { flex: 1, paddingVertical: 9, borderRadius: 8, alignItems: 'center' },
  tabBarBtnActive:     { backgroundColor: '#fff' },
  tabBarText:          { color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: '500' },
  tabBarTextActive:    { color: '#0e4e96', fontWeight: '700' },
  // Featured carousel
  featuredSection:     { marginBottom: 16 },
  featuredTitle:       { color: '#fff', fontWeight: 'bold', fontSize: 15, marginBottom: 10 },
  featuredCard:        { width: 220, backgroundColor: '#fff', borderRadius: 12, marginRight: 12, overflow: 'hidden', elevation: 3, shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 6 },
  featuredCardBanner:  { height: 80, justifyContent: 'center', alignItems: 'center' },
  featuredCardBody:    { padding: 12 },
  featuredCardTitle:   { fontSize: 13, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 4 },
  featuredCardDate:    { fontSize: 12, color: '#0e4e96', marginBottom: 2 },
  featuredCardLocation:{ fontSize: 11, color: '#666', marginBottom: 8 },
  featuredPriceBadge:  { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  featuredPriceText:   { fontSize: 11, fontWeight: '700' },
  // List header
  listHeader:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  listCount:           { color: '#fff', fontSize: 13, fontWeight: '600' },
  sortChip:            { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14 },
  sortChipText:        { fontSize: 12, color: '#0e4e96', marginLeft: 4, fontWeight: '600' },
  // Event card (list)
  eventCard:           { backgroundColor: '#fff', borderRadius: 14, marginBottom: 14, overflow: 'hidden', elevation: 3, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 6 },
  eventCardTop:        { flexDirection: 'row', padding: 14 },
  eventIconContainer:  { width: 48, height: 48, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  eventHeaderContent:  { flex: 1 },
  eventTitleRow:       { flexDirection: 'row', alignItems: 'flex-start' },
  eventTitle:          { fontSize: 15, fontWeight: 'bold', color: '#1a1a2e', flex: 1, lineHeight: 20 },
  saveBtn:             { padding: 2, marginLeft: 4 },
  eventOrganizer:      { fontSize: 12, color: '#666', marginTop: 3, marginBottom: 6 },
  badgeRow:            { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  sourceBadgeSmall:    { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  sourceBadgeText:     { fontSize: 10, fontWeight: '700' },
  featuredBadge:       { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fef3c7', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  featuredBadgeText:   { fontSize: 10, color: '#92400e', fontWeight: '700', marginLeft: 2 },
  onlineBadge:         { backgroundColor: '#e0f2fe', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  onlineBadgeText:     { fontSize: 10, color: '#0369a1', fontWeight: '700' },
  eventDetails:        { paddingHorizontal: 14, paddingBottom: 10, backgroundColor: '#fafafa' },
  eventDetail:         { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  eventDetailText:     { fontSize: 12, color: '#555', marginLeft: 6, flex: 1 },
  eventTags:           { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 14, paddingBottom: 8 },
  tag:                 { backgroundColor: '#f0f4ff', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, marginRight: 6, marginBottom: 4 },
  tagText:             { fontSize: 10, color: '#0e4e96', fontWeight: '500' },
  eventActions:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  priceBadgeLarge:     { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  priceTextLarge:      { fontSize: 13, fontWeight: '700' },
  actionButtons:       { flexDirection: 'row', gap: 8 },
  registerButton:      { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8 },
  registeredButton:    { backgroundColor: '#4CAF50' },
  registerButtonText:  { color: '#fff', fontSize: 12, fontWeight: '700', marginLeft: 4 },
  viewButton:          { flexDirection: 'row', alignItems: 'center', borderWidth: 1.5, borderColor: '#0e4e96', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8 },
  viewButtonText:      { color: '#0e4e96', fontSize: 12, fontWeight: '700' },
  // Grid view
  gridContainer:       { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  gridItem:            { width: (width - 40) / 2 },
  gridCard:            { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', elevation: 3, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 6 },
  gridCardBanner:      { height: 70, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  gridSaveBtn:         { position: 'absolute', top: 6, right: 6 },
  gridCardBody:        { padding: 10 },
  gridCardTitle:       { fontSize: 13, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 4, lineHeight: 18 },
  gridCardDate:        { fontSize: 11, color: '#0e4e96', marginBottom: 2 },
  gridCardLocation:    { fontSize: 11, color: '#888', marginBottom: 8 },
  gridCardFooter:      { flexDirection: 'row', alignItems: 'center', gap: 4 },
  priceBadge:          { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8 },
  priceText:           { fontSize: 10, fontWeight: '700' },
  // Loading / empty
  loadingContainer:    { alignItems: 'center', paddingVertical: 60 },
  loadingText:         { color: 'rgba(255,255,255,0.75)', marginTop: 12, fontSize: 14 },
  noEventsContainer:   { alignItems: 'center', paddingVertical: 60 },
  noEventsText:        { color: '#fff', fontSize: 18, fontWeight: 'bold', marginTop: 16, marginBottom: 6 },
  noEventsSubtext:     { color: 'rgba(255,255,255,0.65)', fontSize: 13, textAlign: 'center', marginBottom: 20 },
  retryButton:         { backgroundColor: '#fff', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 },
  retryButtonText:     { color: '#0e4e96', fontWeight: 'bold', fontSize: 14 },
  eventsList:          { paddingBottom: 10 },
  // Sort modal
  modalOverlay:        { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sortModalContent:    { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24 },
  sortModalTitle:      { fontSize: 18, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 16 },
  sortOption:          { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  sortOptionActive:    { },
  sortOptionText:      { flex: 1, fontSize: 15, color: '#444', marginLeft: 12 },
  sortOptionTextActive:{ color: '#0e4e96', fontWeight: '700' },
  // AI Modal
  modalContainer:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  aiModalContent:      { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '85%', paddingBottom: 20 },
  modalHeader:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  modalHeaderLeft:     { flexDirection: 'row', alignItems: 'center' },
  aiIcon:              { width: 36, height: 36, borderRadius: 18, backgroundColor: '#19abb5', justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  modalTitle:          { fontSize: 17, fontWeight: 'bold', color: '#1a1a2e' },
  aiContextRow:        { flexDirection: 'row', padding: 16, flexWrap: 'wrap', gap: 6 },
  aiContextBadge:      { backgroundColor: '#e0f2fe', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  aiContextText:       { fontSize: 12, color: '#0369a1', fontWeight: '600' },
  aiScrollBody:        { paddingHorizontal: 20, maxHeight: 380 },
  aiLoadingBox:        { alignItems: 'center', paddingVertical: 40 },
  aiLoadingText:       { color: '#666', marginTop: 12, fontSize: 14 },
  aiAdviceText:        { fontSize: 14, color: '#333', lineHeight: 22, marginBottom: 20 },
  tipsContainer:       { backgroundColor: '#f8f9fa', borderRadius: 12, padding: 16, marginBottom: 16 },
  tipsTitle:           { fontWeight: 'bold', color: '#333', marginBottom: 10, fontSize: 15 },
  tipRow:              { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  tipText:             { fontSize: 13, color: '#444', marginLeft: 8, flex: 1, lineHeight: 18 },
  aiRefreshBtn:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#19abb5', marginHorizontal: 20, paddingVertical: 10, borderRadius: 10, marginBottom: 10 },
  aiRefreshText:       { color: '#fff', fontWeight: '700', marginLeft: 6, fontSize: 14 },
  modalButton:         { backgroundColor: '#0e4e96', marginHorizontal: 20, paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  modalButtonText:     { color: '#fff', fontWeight: 'bold', fontSize: 16 },
});
